import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MapPin, Shield, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface LocationSettings {
  enabled: boolean;
  trackingMode: 'login' | 'realtime' | 'disabled';
  dataRetention: number; // في الأيام
  requireConsent: boolean;
  highAccuracy: boolean;
}

export const LocationSettings = () => {
  const [settings, setSettings] = useState<LocationSettings>({
    enabled: false,
    trackingMode: 'disabled',
    dataRetention: 30,
    requireConsent: true,
    highAccuracy: false
  });

  useEffect(() => {
    const savedSettings = localStorage.getItem('location-settings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem('location-settings', JSON.stringify(settings));
    toast.success('تم حفظ إعدادات الموقع بنجاح');
  };

  return (
    <div className="space-y-6">
      <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-2xl transition-all duration-300">
        <CardHeader className="border-b border-gradient-to-r from-transparent via-success/20 to-transparent">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-success to-success/80 shadow-lg shadow-success/30">
              <MapPin className="h-5 w-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">إعدادات تتبع الموقع الجغرافي</span>
          </CardTitle>
          <CardDescription className="text-base">
            تحكم في كيفية جمع واستخدام بيانات الموقع الجغرافي للمستخدمين
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 p-6">
          <div className="p-4 bg-gradient-to-br from-warning/10 to-warning/5 border border-warning/20 rounded-xl shadow-md">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-warning/10">
                <Shield className="h-5 w-5 text-warning" />
              </div>
              <p className="text-sm text-foreground/80 leading-relaxed">
                يتم الآن تعطيل تتبع الموقع الجغرافي افتراضياً لحماية خصوصية المستخدمين. 
                يمكنك تفعيله فقط عند الضرورة القصوى مع موافقة المستخدم.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="location-enabled">تفعيل تتبع الموقع</Label>
                <p className="text-sm text-muted-foreground">
                  السماح للنظام بطلب موقع المستخدم
                </p>
              </div>
              <Switch
                id="location-enabled"
                checked={settings.enabled}
                onCheckedChange={(checked) => 
                  setSettings({ ...settings, enabled: checked })
                }
              />
            </div>

            {settings.enabled && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="tracking-mode">وضع التتبع</Label>
                  <Select
                    value={settings.trackingMode}
                    onValueChange={(value) => 
                      setSettings({ ...settings, trackingMode: value as any })
                    }
                  >
                    <SelectTrigger id="tracking-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="disabled">معطل</SelectItem>
                      <SelectItem value="login">عند تسجيل الدخول فقط</SelectItem>
                      <SelectItem value="realtime">تتبع مستمر (غير مستحسن)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    اختر متى يتم جمع بيانات الموقع
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="require-consent">طلب موافقة المستخدم</Label>
                    <p className="text-sm text-muted-foreground">
                      إظهار نافذة موافقة قبل جمع بيانات الموقع
                    </p>
                  </div>
                  <Switch
                    id="require-consent"
                    checked={settings.requireConsent}
                    onCheckedChange={(checked) => 
                      setSettings({ ...settings, requireConsent: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="high-accuracy">دقة عالية</Label>
                    <p className="text-sm text-muted-foreground">
                      استخدام GPS للحصول على موقع أكثر دقة
                    </p>
                  </div>
                  <Switch
                    id="high-accuracy"
                    checked={settings.highAccuracy}
                    onCheckedChange={(checked) => 
                      setSettings({ ...settings, highAccuracy: checked })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data-retention">مدة الاحتفاظ بالبيانات</Label>
                  <Select
                    value={settings.dataRetention.toString()}
                    onValueChange={(value) => 
                      setSettings({ ...settings, dataRetention: parseInt(value) })
                    }
                  >
                    <SelectTrigger id="data-retention">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 أيام</SelectItem>
                      <SelectItem value="30">30 يوم</SelectItem>
                      <SelectItem value="90">90 يوم</SelectItem>
                      <SelectItem value="180">180 يوم</SelectItem>
                      <SelectItem value="365">سنة واحدة</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    المدة التي يتم فيها الاحتفاظ ببيانات الموقع قبل حذفها تلقائياً
                  </p>
                </div>
              </>
            )}
          </div>

          <Button onClick={handleSave} className="w-full bg-gradient-to-r from-success to-success/80 hover:shadow-lg hover:scale-105 transition-all">
            حفظ الإعدادات
          </Button>
        </CardContent>
      </Card>

      <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-2xl transition-all duration-300">
        <CardHeader className="border-b border-gradient-to-r from-transparent via-info/20 to-transparent">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-info to-info/80 shadow-lg shadow-info/30">
              <AlertCircle className="h-5 w-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">معلومات مهمة حول الخصوصية</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          <div className="p-5 border border-warning/30 bg-gradient-to-br from-warning/10 to-warning/5 rounded-xl shadow-md">
            <h4 className="font-semibold mb-3 text-foreground flex items-center gap-2">
              <span className="text-warning text-lg">⚠️</span>
              ممارسات الخصوصية الموصى بها:
            </h4>
            <ul className="text-sm text-foreground/80 space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-warning mt-0.5">•</span>
                <span>جمع بيانات الموقع فقط عند الضرورة القصوى للوظائف الأساسية</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-warning mt-0.5">•</span>
                <span>الحصول على موافقة صريحة من المستخدم قبل التتبع</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-warning mt-0.5">•</span>
                <span>حذف البيانات القديمة بشكل دوري</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-warning mt-0.5">•</span>
                <span>إعطاء المستخدمين خيار إلغاء الاشتراك في أي وقت</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-warning mt-0.5">•</span>
                <span>تشفير جميع بيانات الموقع المخزنة</span>
              </li>
            </ul>
          </div>

          <div className="p-4 bg-gradient-to-br from-info/10 to-info/5 border border-info/20 rounded-xl shadow-md">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-info/10">
                <Clock className="h-5 w-5 text-info" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-1">سياسة الاحتفاظ بالبيانات:</p>
                <p className="text-sm text-foreground/80">
                  يتم حذف بيانات الموقع تلقائياً بعد انتهاء فترة الاحتفاظ المحددة.
                  البيانات الحالية المخزنة: 0 سجل (تم تنظيف جميع البيانات القديمة)
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};